import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import StartScreen from './components/StartScreen';
import GameBoard from './components/GameBoard';
import RankingBoard from './components/RankingBoard';
import ResultPanel from './components/ResultPanel';

import { useGameState } from './hooks/useGameState';


const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f5f5f5;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const GameContainer = styled.div`
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 10px;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
`;

interface NavButtonProps {
  $active?: boolean;
}

const NavButton = styled.button<NavButtonProps>`
  padding: 10px 20px;
  border: 2px solid ${props => props.$active ? '#667eea' : '#ddd'};
  background: ${props => props.$active ? '#667eea' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$active ? '#5a6fd8' : '#f0f0f0'};
    border-color: ${props => props.$active ? '#5a6fd8' : '#ccc'};
  }
`;

const GameSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

type GameMode = 'menu' | 'game' | 'ranking';

const App: React.FC = () => {
  const [gameMode, setGameMode] = useState<GameMode>('menu');
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState({ reactionTime: 0, score: 0 });
  
  const { 
    status, 
    currentScore, 
    round, 
    startGame, 
    resetGame
  } = useGameState();

  // useEffect 제거 - GameBoard에서 직접 처리

  const handleStartGame = () => {
    console.log('App - handleStartGame called');
    setGameMode('game');
    startGame();
  };

  const handleRoundComplete = (reactionTime: number, score: number) => {
    // 라운드 완료 처리 - 게임 종료는 handleGameComplete에서 처리
    console.log('Round completed:', reactionTime, score);
  };

  const handleGameComplete = (bestReactionTime: number, finalScore: number) => {
    console.log('Game completed with best time:', bestReactionTime, 'final score:', finalScore);
    
    // 이미 팝업이 떠 있는지 확인
    if (showResult) {
      console.log('Result already showing, ignoring...');
      return;
    }
    
    setLastResult({ reactionTime: bestReactionTime, score: finalScore });
    setShowResult(true);
  };

  const handlePlayAgain = () => {
    setShowResult(false);
    setLastResult({ reactionTime: 0, score: 0 });  // 결과 초기화
    setGameMode('game');  // 게임 모드로 설정
    resetGame();  // 게임 상태 초기화
    setTimeout(() => {
      startGame();  // 약간의 지연 후 게임 시작
    }, 100);
  };

  const handleMainMenu = () => {
    setShowResult(false);
    setGameMode('menu');
    resetGame();
  };

  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <AnimatePresence mode="wait">
          {gameMode === 'menu' && (
            <StartScreen key="start" onStart={handleStartGame} />
          )}
          
          {gameMode === 'game' && (
            <GameContainer key="game">
              <Header>
                <Title>반응속도 테스트</Title>
                <Subtitle>게임 진행 중</Subtitle>
              </Header>

              <Navigation>
                <NavButton 
                  $active={true}
                  onClick={() => setGameMode('game')}
                >
                  게임
                </NavButton>
                <NavButton 
                  onClick={() => setGameMode('ranking')}
                >
                  랭킹
                </NavButton>
                <NavButton 
                  onClick={handleMainMenu}
                >
                  홈
                </NavButton>
              </Navigation>

              <GameSection
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <GameBoard 
                  onRoundComplete={handleRoundComplete}
                  onGameComplete={handleGameComplete}
                  gameStatus={status}
                  gameRound={round}
                  gameScore={currentScore}
                  onGameStart={startGame}
                />
              </GameSection>

              <RankingBoard show={false} />
            </GameContainer>
          )}
          
          {gameMode === 'ranking' && (
            <GameContainer key="ranking">
              <Header>
                <Title>🏆 랭킹 보드</Title>
              </Header>

              <Navigation>
                <NavButton 
                  onClick={() => setGameMode('game')}
                >
                  게임
                </NavButton>
                <NavButton 
                  $active={true}
                  onClick={() => setGameMode('ranking')}
                >
                  랭킹
                </NavButton>
                <NavButton 
                  onClick={handleMainMenu}
                >
                  홈
                </NavButton>
              </Navigation>

              <RankingBoard show={true} />
            </GameContainer>
          )}
        </AnimatePresence>

        <ResultPanel
          show={showResult}
          reactionTime={lastResult.reactionTime}
          score={lastResult.score}
          onPlayAgain={handlePlayAgain}
          onMainMenu={handleMainMenu}
        />
      </AppContainer>
    </>
  );
};

export default App;